import type {View} from '@sail/engine';
import {createViewConfig, view, assignProps, animate} from '@sail/engine';
import React, {useEffect, useMemo} from 'react';
import type {FocusScopeProps} from '@sail/react-aria';
import {FocusScope} from '@sail/react-aria';
import type {MobileDisplay} from '../../utilStyles/sheet';
import {
  bottomSheetAnimation,
  bottomSheetStyles,
  fullscreenAnimation,
  fullscreenSheetStyles,
} from '../../utilStyles/sheet';
import {useNestedSheets} from './useNestedSheets';
import {Layer} from '../layers/Layer';
import {useUiConfig} from '../../providers/UiConfigProvider';
import {useOverlayIframe} from '../../iframe-overrides/OverlayOverrides';
import {BottomSheetOverlay} from './BottomSheetOverlay';
import {Backdrop} from '../Backdrop';

type BottomSheetProps = View.IntrinsicElement<
  'div',
  {
    contentWrapperRef: React.RefObject<HTMLDivElement>;
    closeConfirmationDialog: React.ReactNode;
    requestClose?: () => void;
    mobileDisplay: MobileDisplay;
    subviews?: View.Subviews<{
      layer: typeof Layer;
      backdrop: typeof Backdrop;
    }>;
    FocusScopeWrapper?: React.FC<React.PropsWithChildren>;
    focusScopeProps: Required<Omit<FocusScopeProps, 'children'>>;
    layerName?: string;
    shouldShowBackdrop: boolean;
    shouldAnimate: boolean;
  }
>;

export const BottomSheetConfig = createViewConfig({
  props: {} as BottomSheetProps,
  name: 'BottomSheet',
});

const ANIMATION_DURATION_SHORT = 100;
const ANIMATION_DURATION_LONG = 300;
const ANIMATION_EASING = 'cubic-bezier(0, .09, .4, 1)';

/**
 * This is a Sail-internal component that is used in Overlays to render a bottom sheet on mobile/small screens.
 */
export const BottomSheet = BottomSheetConfig.createView(
  ({
    contentWrapperRef,
    children,
    requestClose,
    closeConfirmationDialog,
    focusScopeProps,
    layerName,
    mobileDisplay,
    shouldShowBackdrop,
    shouldAnimate,
    subviews,
    FocusScopeWrapper = React.Fragment,
    ...props
  }) => {
    const uiConfig = useUiConfig();
    const shouldApplyIframeOverrides = !!uiConfig?._unstable_requestIframe;

    const {sheetStackProvider} = useNestedSheets(
      contentWrapperRef,
      mobileDisplay,
    );

    function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        requestClose?.();
      }
    }

    useEffect(() => {
      const contentWrapper = contentWrapperRef.current;
      if (contentWrapper && !contentWrapper.contains(document.activeElement)) {
        // eslint-disable-next-line @sail/ui-no-reflow
        contentWrapper.focus();
      }
    }, [contentWrapperRef]);

    const mobileStyles = useMemo(() => {
      return mobileDisplay === 'bottom-sheet'
        ? bottomSheetStyles
        : fullscreenSheetStyles;
    }, [mobileDisplay]);

    return (
      <Layer
        name={layerName}
        iosScrollLock
        inherits={subviews.layer}
        uses={
          shouldApplyIframeOverrides
            ? [assignProps(useOverlayIframe)]
            : undefined
        }
      >
        {shouldShowBackdrop ? (
          <Backdrop
            inherits={subviews.backdrop}
            onClose={requestClose}
            uses={[
              animate.in({
                duration: ANIMATION_DURATION_SHORT,
                opacity: 0,
                easing: ANIMATION_EASING,
              }),
              animate.out({
                duration: ANIMATION_DURATION_LONG,
                opacity: 0,
                easing: ANIMATION_EASING,
              }),
            ]}
          />
        ) : null}

        <FocusScopeWrapper>
          <FocusScope {...focusScopeProps}>
            <view.div
              onKeyDown={onKeyDown}
              {...props}
              ref={contentWrapperRef}
              uses={[
                mobileDisplay === 'bottom-sheet' ? sheetStackProvider : null,
                mobileStyles,
                bottomSheetAnimation(
                  !shouldAnimate || mobileDisplay !== 'bottom-sheet',
                ),
                fullscreenAnimation(
                  !shouldAnimate || mobileDisplay !== 'fullscreen-sheet',
                ),
              ]}
            >
              <BottomSheetOverlay />

              {children}

              {closeConfirmationDialog}
            </view.div>
          </FocusScope>
        </FocusScopeWrapper>
      </Layer>
    );
  },
);
